import { Heading } from '@innovationnorway/cl-react';
import classNames from 'classnames';
import React from 'react';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import useDocumentTitle from 'src/hooks/use-document-title';
import RouteBreadcrumbs from 'src/components/RouteBreadcrumbs/RouteBreadcrumbs';
import css from './ContentPage.module.scss';

export type ContentPageWidth = 'readable' | 'wide' | 'full';

/** 'tinted' puts the page on grey so Card children read as white cards. */
export type ContentPageSurface = 'plain' | 'tinted';

type Props = React.PropsWithChildren<{
  title: string;
  /** Overrides the browser tab title when it should differ from the visible heading. */
  documentTitle?: string;
  lead?: React.ReactNode;
  /** Rendered inline, immediately after the heading text. */
  titleAdornment?: React.ReactNode;
  /** Rendered in the left margin on wide screens, above the content when narrow. */
  aside?: React.ReactNode;
  width?: ContentPageWidth;
  surface?: ContentPageSurface;
  /** Replaces the auto-derived label for the last breadcrumb, for ids and other dynamic segments. */
  breadcrumbLeaf?: string;
  hideBreadcrumbs?: boolean;
}>;

const ContentPage = ({
  title,
  documentTitle,
  lead,
  titleAdornment,
  aside,
  width = 'readable',
  surface = 'plain',
  breadcrumbLeaf,
  hideBreadcrumbs = false,
  children,
}: Props) => {
  const { documentTitle: resolvedDocumentTitle } = useDocumentTitle(documentTitle ?? title);

  return (
    <main className={classNames(css['reporting-page'], css[`reporting-page--${surface}`])}>
      <DocumentTitle title={resolvedDocumentTitle} />
      <div className={classNames(css['inner'], css[`inner--${width}`])}>
        {!hideBreadcrumbs && <RouteBreadcrumbs leaf={breadcrumbLeaf} />}
        <header className={css['header']}>
          <div className={css['header__title']}>
            <Heading level={1}>{title}</Heading>
            {titleAdornment}
          </div>
          {lead && <p className={css['header__lead']}>{lead}</p>}
        </header>
        <div className={classNames(aside && css['columns--with-aside'])}>
          {aside && <div className={css['aside']}>{aside}</div>}
          <div className="stack">{children}</div>
        </div>
      </div>
    </main>
  );
};

export default ContentPage;
